import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Utensils, UtensilsCrossed, Compass, Flower, Heart, Award, Bike, Sunset, MapPin, X, Phone, Globe, Wifi, Instagram, Facebook } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { fetchRestaurant } from "../lib/api";
import { T, tr } from "../lib/i18n";
import { GiftPill } from "./gift/GiftBits";
import { ReferralPill, REF_PATHS } from "./loyalty/Referral";

const GIFT_BY_PATH = { "/prenota": "cena", "/spa": "spa", "/massaggi": "massaggio", "/cavallo": "cavallo", "/bici": "bici" };

/**
 * Sub-page navigation used on every non-home page:
 *   Row 1: two wide pills — Info (opens drawer) + Dintorni (link)
 *   Row 2: 5 icon cards from 6 rotating destinations —
 *     Menu · Spa · Massaggi · Bici · Cavallo · Fidelity
 *   The card matching the current route is omitted so 5 stay visible.
 *   On /dintorni the "Dintorni" pill disappears and Info stretches full width.
 */
const DESTS = [
  { key: "prenota",  to: "/prenota",  labelKey: "prenota",  Icon: UtensilsCrossed, external: false, highlight: true },
  { key: "menu",     to: "/",         labelKey: "menu",     Icon: Utensils, external: false },
  { key: "spa",      to: "/spa",      labelKey: "spa",      Icon: Flower,   external: false },
  { key: "massaggi", to: "/massaggi", labelKey: "massaggi", Icon: Heart,    external: false },
  { key: "bici",     to: "/bici",     labelKey: "bici",     Icon: Bike,     external: false },
  { key: "cavallo",  to: "/cavallo",  labelKey: "cavallo",  Icon: Sunset,   external: false },
  { key: "fidelity", to: "/fidelity-app", labelKey: "fidelity", Icon: Award, external: false },
];

export function SubPageNav({ hideMenu = false, hideDests = false, variant = "cards", lang = "it" }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const [restaurant, setRestaurant] = useState(null);

  useEffect(() => {
    if (infoOpen && !restaurant) {
      fetchRestaurant().then(setRestaurant).catch(() => {});
    }
  }, [infoOpen, restaurant]);

  const onDintorni = pathname === "/dintorni";
  // Filter out the current route; leaves 4 out of the 5 destinations
  // On MenuPage (/menu/:id) we treat "menu" as current so it stays out too.
  // On /dintorni the "Home" button lives next to "Usa la mia posizione", so Menu is dropped here too.
  const isMenuDetail = pathname.startsWith("/menu/");
  const dropMenu = isMenuDetail || hideMenu || onDintorni;
  const finalItems = DESTS.filter((d) => (d.external || d.to !== pathname) && !(dropMenu && d.key === "menu")).slice(0, 5);

  const infoDrawer = (
    <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-[#4a1c17] bg-[#f7ebdf]">
        <div className="bg-[#4a1c17] text-[#f7ebdf] p-4 flex items-center justify-between">
          <h2 className="font-serif text-lg font-bold">Il Pozzo dei Giganti</h2>
          <button onClick={() => setInfoOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#2b1410]">
          {(() => {
            const info = restaurant?.info || {};
            const address = restaurant?.address || info.address;
            const phone = restaurant?.phone || info.phone;
            const website = restaurant?.website || info.website;
            const wifiPwd = restaurant?.wifi_password || info.wifi?.password;
            return (
              <>
                {address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-[#4a1c17] shrink-0 mt-0.5" />
                    <span>{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-[#4a1c17] shrink-0" />
                    <a href={`tel:${phone}`} className="underline">{phone}</a>
                  </div>
                )}
                {website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[#4a1c17] shrink-0" />
                    <a href={website} target="_blank" rel="noreferrer" className="underline break-all">{website}</a>
                  </div>
                )}
                {wifiPwd && (
                  <div className="flex items-center gap-2">
                    <Wifi className="h-4 w-4 text-[#4a1c17] shrink-0" />
                    <span>Wi-Fi · password <strong>{wifiPwd}</strong> <span className="text-[#7c5a4e]">(valida per tutte le Wi-Fi dei Giganti)</span></span>
                  </div>
                )}
                {/* Social links — always visible, one click to /social with QR + follow shortcuts */}
                <div className="pt-3 mt-3 border-t border-[#e8dcc8]">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#c48b31] mb-2">
                    {tr({ it: "Seguici", en: "Follow us", es: "Síguenos", fr: "Suivez-nous", de: "Folge uns" }, lang)}
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to="/social"
                      onClick={() => setInfoOpen(false)}
                      data-testid="info-social-link"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <Instagram className="h-4 w-4" />
                      <Facebook className="h-4 w-4" />
                      <span>Instagram + Facebook</span>
                    </Link>
                  </div>
                </div>
              </>
            );
          })()}
          {!restaurant && <div className="text-[#7c5a4e] text-xs">Caricamento…</div>}
        </div>
      </DialogContent>
    </Dialog>
  );

  // Compact horizontal strip variant — sits below the top brown bar and
  // packs Info + Dintorni + destinations on a single scrollable row.
  if (variant === "strip") {
    const pillCls = "inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors";
    return (
      <div className="bg-[#3a1b18] text-[#f7ebdf] border-b border-white/10" data-testid="subpage-nav-strip">
        <div className="max-w-5xl mx-auto px-3 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button type="button" onClick={() => setInfoOpen(true)} data-testid="subpage-info-pill" className={pillCls}>
            <MapPin className="h-3.5 w-3.5" /> Info
          </button>
          {!onDintorni && (
            <button type="button" onClick={() => navigate("/dintorni")} data-testid="subpage-dintorni-pill" className={pillCls}>
              <Compass className="h-3.5 w-3.5" /> Dintorni
            </button>
          )}
          {finalItems.map(({ key, to, labelKey, Icon, external }) => {
            const inner = <><Icon className="h-3.5 w-3.5" /> {tr(T[labelKey], lang)}</>;
            if (external) {
              return <a key={key} href={to} target="_blank" rel="noopener noreferrer" data-testid={`subpage-nav-${key}`} className={pillCls}>{inner}</a>;
            }
            return <Link key={key} to={key === "prenota" ? "/prenota?ref=app" : to} data-testid={`subpage-nav-${key}`} className={pillCls}>{inner}</Link>;
          })}
        </div>
        {infoDrawer}
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Row 1 — Info + Dintorni pills. Hidden entirely on /dintorni to avoid
          the leaflet map (fixed z-index) intercepting the Info drawer clicks. */}
      {!onDintorni && (
      <div className="grid grid-cols-2 gap-2 mb-2">
        <button
          type="button"
          onClick={() => setInfoOpen(true)}
          data-testid="subpage-info-pill"
          className="flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#faf3ee] border border-[#e8dcc8] py-2.5 px-4 transition-colors shadow-sm"
        >
          <MapPin className="h-4 w-4 text-[#4a1c17]" />
          <span className="font-serif text-sm font-semibold text-[#2b1410]">{tr(T.info, lang)}</span>
        </button>
        <button
          type="button"
          onClick={() => navigate("/dintorni")}
          data-testid="subpage-dintorni-pill"
          className="flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#faf3ee] border border-[#e8dcc8] py-2.5 px-4 transition-colors shadow-sm"
        >
          <Compass className="h-4 w-4 text-[#4a1c17]" />
          <span className="font-serif text-sm font-semibold text-[#2b1410]">{tr(T.dintorni, lang)}</span>
        </button>
      </div>
      )}

      {/* Row 2 — up to 5 icon cards */}
      {!hideDests && (
      <div className={`grid gap-2 ${
        finalItems.length === 5 ? "grid-cols-5" :
        finalItems.length === 4 ? "grid-cols-4" :
        finalItems.length === 3 ? "grid-cols-3" :
        finalItems.length === 2 ? "grid-cols-2" : "grid-cols-1"
      }`}>
        {finalItems.map(({ key, to, labelKey, Icon, external, highlight }) => {
          const label = tr(T[labelKey], lang);
          const cls = "flex flex-col items-center justify-center gap-1.5 rounded-2xl border py-3 px-1 text-center transition-colors border-[#e8dcc8] bg-white hover:bg-[#faf3ee]";
          const inner = (
            <>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center ${highlight ? "bg-[#C48B31] text-white" : "bg-[#4a1c17] text-[#f7ebdf]"}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="text-[10px] sm:text-xs font-semibold leading-tight text-[#2b1410]">{label}</div>
            </>
          );
          if (external) {
            return (
              <a key={key} href={to} target="_blank" rel="noopener noreferrer" data-testid={`subpage-nav-${key}`} className={cls}>
                {inner}
              </a>
            );
          }
          return (
            <Link key={key} to={to} data-testid={`subpage-nav-${key}`} className={cls}>
              {inner}
            </Link>
          );
        })}
      </div>
      )}

      {GIFT_BY_PATH[pathname] && <div className="mt-2"><GiftPill element={GIFT_BY_PATH[pathname]} lang={lang} /></div>}
      {REF_PATHS.includes(pathname) && <div className="mt-2"><ReferralPill lang={lang} /></div>}

      {infoDrawer}
    </div>
  );
}
